import type { Role, ScreenKey, Lang } from './types';
import { navLabels } from './i18n';

export interface RoleMeta {
  label: string;
  thLabel: string;
  initial: string;
  land: ScreenKey;
}

const ROLE_META: Record<Role, RoleMeta> = {
  procurement: { label: 'Procurement', thLabel: 'จัดซื้อ', initial: 'M', land: 'dashboard' },
  cco: { label: 'CCO', thLabel: 'CCO', initial: 'W', land: 'exec' },
  insurance: { label: 'Insurer', thLabel: 'ผู้รับประกัน', initial: 'A', land: 'myboard' },
  warehouse: { label: 'Warehouse', thLabel: 'คลังสินค้า', initial: 'K', land: 'whpo' },
  supplier: { label: 'Supplier', thLabel: 'ซัพพลายเออร์', initial: 'A', land: 'myboard' },
  admin: { label: 'Admin', thLabel: 'ผู้ดูแลระบบ', initial: 'S', land: 'exec' },
};

export const ROLE_ORDER: Role[] = ['procurement', 'cco', 'insurance', 'warehouse', 'supplier', 'admin'];

export function roleMeta(r: Role): RoleMeta {
  return ROLE_META[r];
}

/** Every screen key mapped to a clean URL path (all under /app). */
export const SCREEN_PATH: Record<ScreenKey, string> = {
  dashboard: '/app/dashboard',
  rfq: '/app/rfq',
  bidcompare: '/app/rfq/bid-comparison',
  award: '/app/rfq/award',
  doa: '/app/doa',
  po: '/app/po',
  podetail: '/app/po/detail',
  invoice: '/app/invoice',
  match: '/app/invoice/match',
  incoming: '/app/incoming',
  exec: '/app/executive',
  fraud: '/app/fraud',
  audit: '/app/audit',
  myboard: '/app/portal/dashboard',
  whpo: '/app/warehouse/po',
  asl: '/app/suppliers/asl',
  review: '/app/suppliers/review',
  supplierdetail: '/app/suppliers/detail',
  bidsubmit: '/app/portal/bids',
  myorders: '/app/portal/orders',
  feedback: '/app/portal/feedback',
  contract: '/app/contracts',
  contractdetail: '/app/contracts/detail',
  users: '/app/users',
  settings: '/app/settings',
  notifs: '/app/notifications',
  override: '/app/fraud/override',
  rpt: '/app/fraud/rpt',
  network: '/app/fraud/network',
  wbcases: '/app/fraud/whistleblower',
  batch: '/app/insurance-batch',
  inscompare: '/app/insurance-batch/compare',
  bidform: '/app/portal/bids/submit',
  reports: '/app/reports',
  reviewdetail: '/app/suppliers/review/detail',
  supplierpodetail: '/app/portal/orders/detail',
  warehousepodetail: '/app/warehouse/po/detail',
  rfqsend: '/app/incoming/send',
  biddetail: '/app/portal/bids/detail',
};

export const PATH_SCREEN: Record<string, ScreenKey> = Object.fromEntries(
  Object.entries(SCREEN_PATH).map(([k, v]) => [v, k as ScreenKey]),
) as Record<string, ScreenKey>;

export function screenFromPath(pathname: string): ScreenKey | null {
  return PATH_SCREEN[pathname] ?? null;
}

/** Parent screen for breadcrumb/active-nav highlighting. */
export const PARENT: Partial<Record<ScreenKey, ScreenKey>> = {
  bidcompare: 'rfq', award: 'rfq', podetail: 'po', match: 'invoice',
  contractdetail: 'contract', inscompare: 'batch', override: 'fraud',
  rpt: 'fraud', network: 'fraud', wbcases: 'fraud', supplierdetail: 'asl',
  bidform: 'bidsubmit', reviewdetail: 'review',
  supplierpodetail: 'myorders', warehousepodetail: 'whpo', rfqsend: 'incoming',
  biddetail: 'bidsubmit',
};

/** [breadcrumb, title, subtitle] per screen. */
export const TITLES: Record<string, [string, string, string]> = {
  dashboard: ['Procurement / Dashboard', 'Procurement Dashboard', 'Overview of intake, bidding, approvals, and supplier reviews.'],
  rfq: ['Procurement / RFQ Dashboard', 'RFQ Management', 'All requests for quotation across their lifecycle.'],
  bidcompare: ['Procurement / RFQ Management / Bid Comparison', 'Bid Comparison — RFQ-2026-001', 'Blind evaluation. Supplier identities are masked until award.'],
  award: ['Procurement / RFQ Management / Award Decision', 'Award Decision — RFQ-2026-001', 'Confirm the winning bid and generate the purchase order.'],
  doa: ['Approval / DOA Approval', 'DOA Approval', 'Delegation-of-authority approvals awaiting your e-signature.'],
  po: ['Procurement / PO Management', 'PO Management', 'Purchase orders across their lifecycle, from issue to payment.'],
  podetail: ['Procurement / PO Management / PO-2026-0041', 'PO-2026-0041', 'Fleet Vehicle Insurance — Class 1, Policy Period 2026-2027.'],
  invoice: ['Procurement / Invoice Matching', 'Invoice Matching', 'Reconcile purchase orders, goods receipts, and invoices.'],
  match: ['Procurement / Invoice Matching / PO-2026-0041', '3-Way Match — PO-2026-0041', 'PO, goods receipt, and invoice reconciliation.'],
  incoming: ['Procurement / Incoming PR / RFQ', 'Incoming PR / RFQ', 'Purchase requisitions awaiting review and RFQ creation.'],
  exec: ['Executive / Dashboard', 'Executive Dashboard', 'Spend, savings, and compliance at a glance.'],
  fraud: ['Compliance / Fraud Dashboard', 'Fraud Dashboard', 'Collusion, related-party, and split-order detection.'],
  audit: ['Compliance / Audit Trail', 'Audit Trail', 'Immutable, hash-verified record of every action.'],
  myboard: ['Supplier Portal / My Dashboard', 'My Dashboard', 'Apex Insurance Co. · SUP-001'],
  whpo: ['Operations / Purchase Orders', 'Purchase Orders', 'Record goods receipts and upload invoices. Kris P.'],
  asl: ['Suppliers / ASL Management', 'ASL Management', 'Approved Supplier List with performance and compliance signals.'],
  review: ['Suppliers / Supplier Review', 'Supplier Review', 'New supplier registrations awaiting verification.'],
  supplierdetail: ['Suppliers / ASL Management / SUP-006', 'Pacific Insurance Co.', 'SUP-006 · Insurance · Tier 2'],
  bidsubmit: ['Supplier Portal / Bid Submission', 'Bid Submission', 'Respond to RFQ invitations and track your bids.'],
  myorders: ['Supplier Portal / My Purchase Orders', 'My Purchase Orders', 'Purchase orders awarded to you.'],
  feedback: ['Supplier Portal / Feedback', 'Feedback', 'Share feedback on RFQ outcomes.'],
  contract: ['Procurement / Contract Management', 'Contract Management', 'Active agreements, renewals, and expirations.'],
  users: ['System / User Management', 'User Management', 'Platform users, roles, and access controls.'],
  settings: ['System / Master Settings', 'Master Settings', 'Thresholds, scoring weights, SLAs, and feature flags.'],
  notifs: ['Notifications', 'Notification Center', 'All alerts, actions, and updates.'],
  override: ['Compliance / Fraud Dashboard / Override Log', 'AI Override Log', 'Awards that deviated from the AI recommendation.'],
  rpt: ['Compliance / Fraud Dashboard / RPT Report', 'Related-Party Transaction Report', 'Transactions with group-affiliated parties.'],
  network: ['Compliance / Fraud Dashboard / Network Graph', 'Supplier Network Graph', 'Shared-director links across suppliers.'],
  wbcases: ['Compliance / Fraud Dashboard / Whistleblower Cases', 'Whistleblower Cases', 'Confidential reports — Audit Committee access only.'],
  batch: ['Procurement / Insurance Batch RFQ', 'Insurance Batch RFQ', 'Bundle fleet vehicles into a single insurer RFQ.'],
  inscompare: ['Procurement / Insurance Batch RFQ / Comparison', 'Insurance Comparison', 'Blind comparison of insurer quotes by coverage class.'],
  contractdetail: ['Procurement / Contract Management / CON-2026-0014', 'Fleet Insurance Agreement 2026-2027', 'CON-2026-0014 · Apex Insurance Co.'],
  bidform: ['Supplier Portal / Bid Submission / New Bid', 'Submit Bid — RFQ-2026-004', 'Fleet Vehicle Insurance — Class 2 & 3. Your bid stays sealed until the deadline.'],
  reports: ['Compliance / Report Submissions', 'Report Submissions', 'All whistleblower reports submitted through the public Report Details form.'],
  reviewdetail: ['Suppliers / Supplier Review / Review', 'Review Supplier', 'Verify the registration, then approve with a tier or reject with a reason.'],
  supplierpodetail: ['Supplier Portal / My Purchase Orders / Detail', 'Purchase Order', 'View the purchase order and upload your invoice.'],
  warehousepodetail: ['Operations / Purchase Orders / Detail', 'Goods Receipt & Invoices', 'Record goods receipt and upload supplier invoices.'],
  rfqsend: ['Procurement / Incoming PR / RFQ / Review', 'Review & Send RFQ', 'Confirm the procurement method and AI-recommended suppliers, then send.'],
  biddetail: ['Supplier Portal / Bid Submission / Detail', 'Bid Details', 'Your bid, its evaluation, and any negotiation with procurement.'],
};

/** Thai [breadcrumb, title, subtitle] per screen — mirrors TITLES. */
export const TITLES_TH: Record<string, [string, string, string]> = {
  dashboard: ['จัดซื้อ / แดชบอร์ด', 'แดชบอร์ดจัดซื้อ', 'ภาพรวมการรับเรื่อง การเสนอราคา การอนุมัติ และการตรวจสอบซัพพลายเออร์'],
  rfq: ['จัดซื้อ / แดชบอร์ด RFQ', 'จัดการ RFQ', 'ใบขอเสนอราคา (RFQ) ทั้งหมดตลอดวงจรการทำงาน'],
  bidcompare: ['จัดซื้อ / จัดการ RFQ / เปรียบเทียบข้อเสนอ', 'เปรียบเทียบข้อเสนอ — RFQ-2026-001', 'การประเมินแบบปิด ตัวตนของซัพพลายเออร์จะถูกปิดบังจนกว่าจะมอบสัญญา'],
  award: ['จัดซื้อ / จัดการ RFQ / การตัดสินมอบสัญญา', 'การตัดสินมอบสัญญา — RFQ-2026-001', 'ยืนยันข้อเสนอราคาที่ชนะและสร้างใบสั่งซื้อ (PO)'],
  doa: ['การอนุมัติ / การอนุมัติ DOA', 'การอนุมัติ DOA', 'การอนุมัติตามการมอบอำนาจ (DOA) ที่รอลายเซ็นอิเล็กทรอนิกส์ของคุณ'],
  po: ['จัดซื้อ / จัดการ PO', 'จัดการ PO', 'ใบสั่งซื้อ (PO) ตลอดวงจรการทำงาน ตั้งแต่การออกจนถึงการชำระเงิน'],
  podetail: ['จัดซื้อ / จัดการ PO / PO-2026-0041', 'PO-2026-0041', 'ประกันภัยรถยนต์กองยานพาหนะ — ชั้น 1 ระยะเวลากรมธรรม์ 2026-2027'],
  invoice: ['จัดซื้อ / การจับคู่ใบแจ้งหนี้', 'การจับคู่ใบแจ้งหนี้', 'กระทบยอดใบสั่งซื้อ ใบรับสินค้า และใบแจ้งหนี้'],
  match: ['จัดซื้อ / การจับคู่ใบแจ้งหนี้ / PO-2026-0041', 'การจับคู่ 3 ทาง — PO-2026-0041', 'การกระทบยอดใบสั่งซื้อ ใบรับสินค้า และใบแจ้งหนี้'],
  incoming: ['จัดซื้อ / ใบขอซื้อ (PR) / RFQ ขาเข้า', 'ใบขอซื้อ (PR) / RFQ ขาเข้า', 'ใบขอซื้อ (PR) ที่รอการตรวจสอบและการสร้าง RFQ'],
  exec: ['ผู้บริหาร / แดชบอร์ด', 'แดชบอร์ดผู้บริหาร', 'ค่าใช้จ่าย เงินที่ประหยัดได้ และการปฏิบัติตามข้อกำหนดโดยสรุป'],
  fraud: ['การกำกับดูแล / แดชบอร์ดการทุจริต', 'แดชบอร์ดการทุจริต', 'การตรวจจับการสมรู้ร่วมคิด ผลประโยชน์ทับซ้อน และการแบ่งย่อยคำสั่งซื้อ'],
  audit: ['การกำกับดูแล / บันทึกการตรวจสอบ', 'บันทึกการตรวจสอบ', 'บันทึกทุกการกระทำที่แก้ไขไม่ได้และตรวจสอบด้วยแฮช'],
  myboard: ['พอร์ทัลซัพพลายเออร์ / แดชบอร์ดของฉัน', 'แดชบอร์ดของฉัน', 'Apex Insurance Co. · SUP-001'],
  whpo: ['ปฏิบัติการ / ใบสั่งซื้อ', 'ใบสั่งซื้อ', 'บันทึกใบรับสินค้าและอัปโหลดใบแจ้งหนี้ Kris P.'],
  asl: ['ซัพพลายเออร์ / จัดการรายชื่อผู้ขาย (ASL)', 'จัดการรายชื่อผู้ขาย (ASL)', 'รายชื่อผู้ขายที่ได้รับอนุมัติพร้อมสัญญาณด้านประสิทธิภาพและการปฏิบัติตามข้อกำหนด'],
  review: ['ซัพพลายเออร์ / ตรวจสอบซัพพลายเออร์', 'ตรวจสอบซัพพลายเออร์', 'การลงทะเบียนซัพพลายเออร์รายใหม่ที่รอการยืนยัน'],
  supplierdetail: ['ซัพพลายเออร์ / จัดการรายชื่อผู้ขาย (ASL) / SUP-006', 'Pacific Insurance Co.', 'SUP-006 · ประกันภัย · ระดับ 2'],
  bidsubmit: ['พอร์ทัลซัพพลายเออร์ / ยื่นข้อเสนอราคา', 'ยื่นข้อเสนอราคา', 'ตอบรับคำเชิญ RFQ และติดตามข้อเสนอราคาของคุณ'],
  myorders: ['พอร์ทัลซัพพลายเออร์ / ใบสั่งซื้อของฉัน', 'ใบสั่งซื้อของฉัน', 'ใบสั่งซื้อที่มอบให้แก่คุณ'],
  feedback: ['พอร์ทัลซัพพลายเออร์ / ข้อเสนอแนะ', 'ข้อเสนอแนะ', 'แบ่งปันข้อเสนอแนะเกี่ยวกับผลการพิจารณา RFQ'],
  contract: ['จัดซื้อ / จัดการสัญญา', 'จัดการสัญญา', 'สัญญาที่มีผลบังคับใช้ การต่ออายุ และการหมดอายุ'],
  users: ['ระบบ / จัดการผู้ใช้', 'จัดการผู้ใช้', 'ผู้ใช้แพลตฟอร์ม บทบาท และการควบคุมการเข้าถึง'],
  settings: ['ระบบ / ตั้งค่าระบบ', 'ตั้งค่าระบบ', 'เกณฑ์ น้ำหนักการให้คะแนน SLA และแฟล็กฟีเจอร์'],
  notifs: ['การแจ้งเตือน', 'ศูนย์การแจ้งเตือน', 'การแจ้งเตือน การกระทำ และการอัปเดตทั้งหมด'],
  override: ['การกำกับดูแล / แดชบอร์ดการทุจริต / บันทึกการยกเลิก', 'บันทึกการยกเลิกคำแนะนำ AI', 'การมอบสัญญาที่เบี่ยงเบนจากคำแนะนำของ AI'],
  rpt: ['การกำกับดูแล / แดชบอร์ดการทุจริต / รายงาน RPT', 'รายงานธุรกรรมกับบุคคลที่เกี่ยวข้องกัน (RPT)', 'ธุรกรรมกับบุคคลในกลุ่มที่เกี่ยวข้องกัน'],
  network: ['การกำกับดูแล / แดชบอร์ดการทุจริต / กราฟเครือข่าย', 'กราฟเครือข่ายซัพพลายเออร์', 'ความเชื่อมโยงของกรรมการที่ใช้ร่วมกันระหว่างซัพพลายเออร์'],
  wbcases: ['การกำกับดูแล / แดชบอร์ดการทุจริต / คดีผู้แจ้งเบาะแส', 'คดีผู้แจ้งเบาะแส', 'รายงานลับ — เฉพาะคณะกรรมการตรวจสอบเข้าถึงได้เท่านั้น'],
  batch: ['จัดซื้อ / RFQ ประกันภัยแบบชุด', 'RFQ ประกันภัยแบบชุด', 'รวมยานพาหนะในกองยานเป็น RFQ เดียวสำหรับผู้รับประกันภัย'],
  inscompare: ['จัดซื้อ / RFQ ประกันภัยแบบชุด / เปรียบเทียบ', 'เปรียบเทียบประกันภัย', 'เปรียบเทียบข้อเสนอราคาของผู้รับประกันภัยแบบปิดตามชั้นความคุ้มครอง'],
  contractdetail: ['จัดซื้อ / จัดการสัญญา / CON-2026-0014', 'สัญญาประกันภัยกองยานพาหนะ 2026-2027', 'CON-2026-0014 · Apex Insurance Co.'],
  bidform: ['พอร์ทัลซัพพลายเออร์ / ยื่นข้อเสนอราคา / ข้อเสนอราคาใหม่', 'ยื่นข้อเสนอราคา — RFQ-2026-004', 'ประกันภัยรถยนต์กองยานพาหนะ — ชั้น 2 และ 3 ข้อเสนอราคาของคุณจะถูกปิดผนึกจนถึงกำหนดเส้นตาย'],
  reports: ['การกำกับดูแล / รายงานที่ส่งเข้ามา', 'รายงานที่ส่งเข้ามา', 'รายงานของผู้แจ้งเบาะแสทั้งหมดที่ส่งผ่านแบบฟอร์มรายละเอียดรายงานสาธารณะ'],
  reviewdetail: ['ซัพพลายเออร์ / ตรวจสอบซัพพลายเออร์ / ตรวจสอบ', 'ตรวจสอบซัพพลายเออร์', 'ยืนยันการลงทะเบียน จากนั้นอนุมัติพร้อมระดับหรือปฏิเสธพร้อมเหตุผล'],
  supplierpodetail: ['พอร์ทัลซัพพลายเออร์ / ใบสั่งซื้อของฉัน / รายละเอียด', 'ใบสั่งซื้อ', 'ดูใบสั่งซื้อและอัปโหลดใบแจ้งหนี้ของคุณ'],
  warehousepodetail: ['ปฏิบัติการ / ใบสั่งซื้อ / รายละเอียด', 'ใบรับสินค้าและใบแจ้งหนี้', 'บันทึกใบรับสินค้าและอัปโหลดใบแจ้งหนี้ของซัพพลายเออร์'],
  rfqsend: ['จัดซื้อ / ใบขอซื้อ (PR) / RFQ ขาเข้า / ตรวจสอบ', 'ตรวจสอบและส่ง RFQ', 'ยืนยันวิธีการจัดซื้อและซัพพลายเออร์ที่ AI แนะนำ จากนั้นส่ง'],
  biddetail: ['พอร์ทัลซัพพลายเออร์ / ยื่นข้อเสนอราคา / รายละเอียด', 'รายละเอียดข้อเสนอราคา', 'ข้อเสนอราคาของคุณ การประเมิน และการเจรจากับฝ่ายจัดซื้อ'],
};

/** Select the [breadcrumb, title, subtitle] tuple for a screen by language. */
export function titlesFor(lang: 'en' | 'th', screen: string): [string, string, string] {
  return (lang === 'th' ? TITLES_TH : TITLES)[screen] ?? ['', '', ''];
}

export interface NavItem {
  key: ScreenKey;
  icon: string;
  label: string;
  badge?: string;
}
export interface NavSection {
  label: string;
  items: NavItem[];
}

/** Role-scoped sidebar sections. Labels localise via `lang`. */
export function buildSidebar(role: Role, lang: Lang): NavSection[] {
  const nl = navLabels(lang);
  const S = (enStr: string, thStr: string) => (lang === 'th' ? thStr : enStr);
  const item = (icon: string, key: ScreenKey, badge?: string): NavItem => ({ key, icon, label: nl[key], badge });

  if (role === 'cco') return [
    { label: S('Analytics', 'การวิเคราะห์'), items: [item('layout-dashboard', 'exec')] },
    { label: S('Compliance', 'การกำกับดูแล'), items: [item('shield-alert', 'fraud'), item('history', 'audit')] },
  ];
  if (role === 'admin') return [
    { label: S('Analytics', 'การวิเคราะห์'), items: [item('layout-dashboard', 'exec')] },
    { label: S('Compliance', 'การกำกับดูแล'), items: [item('history', 'audit'), item('flag', 'reports')] },
    { label: S('System', 'ระบบ'), items: [item('users', 'users'), item('settings', 'settings')] },
  ];
  if (role === 'warehouse') return [
    { label: S('Operations', 'ปฏิบัติการ'), items: [item('shopping-cart', 'whpo')] },
  ];
  if (role === 'insurance' || role === 'supplier') {
    const portal = role === 'insurance' ? S('Insurer Portal', 'พอร์ทัลผู้รับประกัน') : S('Supplier Portal', 'พอร์ทัลซัพพลายเออร์');
    return [{ label: portal, items: [item('layout-dashboard', 'myboard'), item('gavel', 'bidsubmit'), item('shopping-cart', 'myorders'), item('star', 'feedback')] }];
  }
  return [
    { label: S('Procurement', 'จัดซื้อ'), items: [item('layout-dashboard', 'dashboard'), item('file-text', 'rfq'), item('inbox', 'incoming'), item('shopping-cart', 'po'), item('receipt', 'invoice'), item('file-signature', 'contract'), item('car', 'batch')] },
    { label: S('Suppliers', 'ซัพพลายเออร์'), items: [item('users', 'asl'), item('user-check', 'review', '3')] },
    { label: S('Approval', 'การอนุมัติ'), items: [item('pen-tool', 'doa')] },
  ];
}
